import * as React from 'react';
import clsx from 'clsx';
import ButtonIcon from './ButtonIcon';
import { LogoEye } from '../svg';
import StateOverlayIcon from './StateOverlayIcon';
import baseTheme from 'tailwind-config/baseTheme';
import { motion } from 'framer-motion';

export type VARIANT_PRIMARY = 'primary';
export type VARIANT_FLOAT = 'float';
export type VARIANT_SECONDARY = 'secondary';
export type VARIANT_TERTIARY = 'tertiary';
export type VARIANT_SURFACE = 'surface';
export type VARIANT_INVERTED = 'inverted';
export type VARIANT_SUCCESS = 'success';
export type VARIANT_TEXT = 'text';

export type ButtonVariant =
  | VARIANT_PRIMARY
  | VARIANT_SECONDARY
  | VARIANT_TERTIARY
  | VARIANT_SURFACE
  | VARIANT_INVERTED
  | VARIANT_SUCCESS
  | VARIANT_TEXT
  | VARIANT_FLOAT;

export const variantFromString = (variant: string | undefined | null): ButtonVariant => {
  if (variant === 'tertiary') {
    return 'tertiary';
  } else if (variant === 'secondary') {
    return 'secondary';
  } else if (variant === 'surface') {
    return 'surface';
  } else if (variant === 'text') {
    return 'text';
  } else if (variant === 'float') {
    return 'float';
  } else {
    return 'primary';
  }
};

type ButtonAttributes = React.ButtonHTMLAttributes<HTMLButtonElement>;

export interface Props extends ButtonAttributes {
  label: string;
  variant?: ButtonVariant;
  disabled?: ButtonAttributes['disabled'];
  onClick?: ButtonAttributes['onClick'];
  classes?: string;
  labelClasses?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  autoWidth?: boolean;
  id?: string;
  type?: 'button' | 'submit' | 'reset';
  bordered?: boolean;
  elevated?: boolean;
  onHover?: (isHover: boolean) => void;
}

const buttonStyles = {
  success:
    'bg-button-success-default disabled:opacity-40 ' +
    'focus-visible:[&:not(:disabled)]:bg-button-success-pressed ' +
    'hover:[&:not(:focus-visible):not(:disabled)]:bg-button-success-hover ',
  primary:
    'bg-button-primary-default disabled:bg-primary-disabled ' +
    'focus-visible:[&:not(:disabled)]:bg-button-primary-pressed ' +
    'hover:[&:not(:focus-visible):not(:disabled)]:bg-button-primary-hover ' +
    'active:[&:not(:focus-visible):not(:disabled)]:bg-button-primary-pressed',
  float: 'bg-transparent',
  secondary:
    'bg-button-secondary-default disabled:bg-primary-disabled ' +
    'focus-visible:[&:not(:disabled)]:bg-button-secondary-pressed ' +
    'hover:[&:not(:focus-visible):not(:disabled)]:bg-button-secondary-hover ' +
    'active:[&:not(:focus-visible):not(:disabled)]:bg-button-secondary-pressed',
  tertiary:
    'bg-button-tertiary-default disabled:bg-primary-disabled ' +
    'hover:border-button-tertiary-hover ' +
    'focus-visible:border-button-tertiary-pressed ' +
    'focus-visible:[&:not(:disabled)]:bg-button-tertiary-pressed ' +
    'hover:[&:not(:focus-visible):not(:disabled)]:bg-button-tertiary-hover ' +
    'active:[&:not(:focus-visible):not(:disabled)]:bg-button-tertiary-pressed',
  surface:
    'bg-button-surface-default ' +
    'hover:border-button-surface-hover ' +
    'focus-visible:border-button-surface-pressed ' +
    'focus-visible:[&:not(:disabled)]:bg-button-surface-pressed ' +
    'hover:[&:not(:focus-visible):not(:disabled)]:bg-button-surface-hover ' +
    'active:[&:not(:focus-visible):not(:disabled)]:bg-button-surface-pressed',
  inverted: '',
  text: 'bg-transparent ',
};

const labelStyles = {
  success: 'label-lg text-white-100 group-disabled:text-primary-disabled-text',
  primary: 'label-lg text-white-100 group-disabled:text-primary-disabled-text',
  float: 'label-lg text-white-100 group-disabled:text-primary-disabled-text',
  secondary: 'label-lg text-white-100 group-disabled:text-primary-disabled-text',
  tertiary: 'label-lg text-white-100 group-disabled:text-primary-disabled-text',
  inverted: 'label-lg text-white-100 group-disabled:text-primary-disabled-text',
  surface:
    'label-lg text-black-100 group-hover:text-primary-red group-focus-visible:text-primary-red group-disabled:text-black-100',
  text: 'label-lg',
};

const overlays = {
  success: '#000000',
  primary: baseTheme.colors.button.primary.overlay,
  float: baseTheme.colors.button.primary.overlay,
  secondary: baseTheme.colors.button.secondary.overlay,
  tertiary: baseTheme.colors.button.tertiary.overlay,
  surface: baseTheme.colors.button.surface.overlay,
  inverted: 'transparent',
  text: baseTheme.colors.button.primary.overlay,
};

const elevatedStyle = {
  boxShadow:
    '0px 54px 15px 0px rgba(24, 41, 52, 0.00), 0px 35px 14px 0px rgba(24, 41, 52, 0.01), 0px 19px 12px 0px rgba(24, 41, 52, 0.03), 0px 9px 9px 0px rgba(24, 41, 52, 0.04), 0px 2px 5px 0px rgba(24, 41, 52, 0.05)',
};

/**
 * Vodafone button
 */
const Button = ({
  label,
  variant = 'primary',
  disabled = false,
  onClick,
  classes,
  labelClasses,
  icon,
  iconPosition = 'left',
  loading = false,
  autoWidth = true,
  id,
  type = 'button',
  bordered = false,
  elevated = false,
  onHover = () => false,
}: Props) => {
  return (
    <motion.button
      type={type}
      id={id}
      className={clsx(
        'group relative inline-flex h-[56px] items-center justify-center gap-8 overflow-hidden rounded-full px-24 py-8 leading-normal disabled:cursor-not-allowed',
        autoWidth ? 'w-fit' : 'w-full',
        disabled && 'cursor-not-allowed',
        buttonStyles[variant],
        labelStyles[variant],
        classes,
        { 'border-1 border-solid border-[#E1E2E3]': bordered }
      )}
      style={elevated ? elevatedStyle : {}}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => {
        onHover(true);
      }}
      onMouseLeave={() => {
        onHover(false);
      }}
      whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
      whileTap={{ scale: 0.98 }}
    >
      {iconPosition === 'left' && <ButtonIcon icon={icon} iconPosition={iconPosition} loading={loading} />}
      {label && (
        <span className={`${labelStyles[variant]} ${labelClasses} ${clsx({ 'mx-24': loading })}`}>
          {loading ? (
            <LogoEye variant={variant === 'tertiary' ? 'classic' : 'inverted'} width={24} height={20} isLoading />
          ) : (
            label
          )}
        </span>
      )}
      {iconPosition === 'right' && <ButtonIcon icon={icon} iconPosition={iconPosition} loading={loading} />}
      <div className="absolute -bottom-56 -right-80 transition-all group-active:bottom-0 group-active:right-0">
        <StateOverlayIcon fill={overlays[variant]} />
      </div>
    </motion.button>
  );
};

export default Button;
